<script setup lang="ts">
    import { flashMessage } from "@/utils";
    import { computed, onBeforeMount, provide, ref, toRefs } from "vue";
    import { useStore } from "@/store";
    import emitter from "tiny-emitter/instance";
    import KForm from "@/components/forms/Form/KForm.vue";
    import { kontaktorsakDependents } from "@/tabs/Operation/utils.js";
    import { useKForm } from "@/composables/useKForm";

    interface KontaktorsakFieldConfig {
        visible: boolean;
        required?: boolean;
    }

    interface KontaktorsakGroupConfig {
        visible: boolean;
    }

    interface KontaktorsakConfig {
        fields: Record<string, KontaktorsakFieldConfig>;
        groups: Record<string, KontaktorsakGroupConfig>;
    }

    // TODO Ändra till användning av type i kontaktorsakModule i Vue3
    interface Kontaktorsak {
        id?: number;
        code?: string;
        displayName: string;
        config: KontaktorsakConfig;
        headertitle?: string;
    }

    const store = useStore();

    const fields = [
        {
            component: "KGroup",
            name: "config.fieldsTitles",
            direction: "horizontal",
            freeFlow: true,
            rowGap: "half",
            children: [
                { component: "KLabel", content: " ", class: "span-all", disabled: true },
                { component: "KLabel", content: "Synlig", disabled: true },
                { component: "KLabel", content: "Obligatoriskt", disabled: true },
            ],
        },
        ...kontaktorsakDependents.fields
            // Filtrerar bort fält som varken har visible eller required
            .filter((field: { visible: boolean; required: boolean }) => field.visible || field.required)
            // Bygger schemat för Vueformulate
            .map((field: { name: string; label: string; visible: boolean; required: boolean }) => {
                let visibleComponent;
                let requiredComponent;

                if (field.visible) {
                    visibleComponent = { component: "KCheckbox", name: `${field.name}.visible`, content: "" };
                } else {
                    visibleComponent = { component: "KLabel", name: `${field.name}.visible`, content: "-" };
                }

                if (field.required) {
                    requiredComponent = { component: "KCheckbox", name: `${field.name}.required`, label: "" };
                } else {
                    requiredComponent = { component: "KLabel", name: `${field.name}.required`, content: "-" };
                }

                return {
                    component: "KGroup",
                    name: `config.fields.${field.name}`,
                    direction: "horizontal",
                    freeFlow: true,
                    rowGap: "half",
                    children: [
                        {
                            component: "KLabel",
                            name: `${field.name}.label`,
                            content: field.label,
                        },
                        visibleComponent,
                        requiredComponent,
                    ],
                };
            }),
    ];
    const groups = kontaktorsakDependents.groups.map((group: { name: string; label: string }) => {
        return { component: "KCheckbox", name: group.name, label: `${group.label} synlig` };
    });
    const schema = [
        {
            component: "SchemaTitle",
            name: "headertitle",
            content: "Skapa kontaktorsak",
        },
        {
            component: "KGroup",
            name: "kontaktorsak",
            label: "",
            direction: "vertical",
            freeFlow: true,
            class: "span-one",
            children: [
                {
                    component: "KText",
                    name: "displayName",
                    label: "Namn",
                    placeholder: "Ange namn på kontaktorsak",
                    validation: "required",
                },
            ],
        },

        {
            component: "KSection",
            name: "field_section",
            heading: "",
            collapsible: false,
            numbered: false,
            children: [
                {
                    component: "div",
                    class: "form-content",
                    children: [
                        {
                            component: "KGroup",
                            name: "config.fields",
                            label: "Fältkonfiguration",
                            direction: "vertical",
                            freeFlow: false,
                            props: {},
                            children: fields,
                        },
                    ],
                },
            ],
        },

        {
            component: "KSection",
            name: "groups_section",
            heading: "",
            collapsible: false,
            numbered: false,
            children: [
                {
                    component: "div",
                    class: "form-content",
                    children: [
                        {
                            component: "KGroup",
                            name: "config.groups",
                            label: "Grupper",
                            direction: "vertical",
                            freeFlow: true,
                            children: groups,
                        },
                    ],
                },
            ],
        },
    ];

    const props = defineProps<{
        tabId: number;
        parameters?: {
            kontaktorsakId?: number;
        };
    }>();

    const { parameters, tabId } = toRefs(props);

    const { inputErrors, formErrors, handleErrorResponse } = useKForm();

    provide("formParent", {
        inputErrors,
    });

    const kontaktorsakId = parameters?.value?.kontaktorsakId ?? null;

    const formRef = ref();

    const initForm = async () => {
        await store.dispatch("tabs/displaySpinner", { id: tabId?.value, display: true });

        let formData: any = {};

        if (kontaktorsakId) {
            const data = store.getters["operation/kontaktorsak/kontaktorsakById"]({
                id: kontaktorsakId,
            });

            formData = responseDataConverter(data);
        } else {
            const data = {
                displayName: "",
                config: {
                    fields: Object.fromEntries(
                        kontaktorsakDependents.fields.map((field: { name: string }) => [
                            field.name,
                            { visible: true, required: false },
                        ])
                    ),
                    groups: Object.fromEntries(
                        kontaktorsakDependents.groups.map((group: { name: string }) => [group.name, { visible: true }])
                    ),
                },
            };
            formData = responseDataConverter(data);
        }

        await store.dispatch("tabs/displaySpinner", { id: tabId?.value, display: false });

        return formData;
    };

    /**
     * Konvterar backenddata till ett format som formuläret vill ha
     */
    function responseDataConverter(data: Kontaktorsak): any {
        const fields = Object.fromEntries(
            kontaktorsakDependents.fields.flatMap((field: { name: string }) => {
                return [
                    [`${field.name}.visible`, Boolean(data?.config?.fields?.[field.name]?.visible ?? true)],
                    [`${field.name}.required`, Boolean(data?.config?.fields?.[field.name]?.required ?? false)],
                ];
            })
        );

        const groups = Object.fromEntries(
            kontaktorsakDependents.groups.map((group: { name: string }) => [
                group.name,
                Boolean(data?.config?.groups?.[group.name]?.visible ?? true),
            ])
        );

        return {
            displayName: data.displayName,
            ...fields,
            ...groups,
        };
    }

    const submitForm = async (formData: any) => {
        let data = formDataConverter(formData);

        try {
            if (kontaktorsakId) {
                await store.dispatch("operation/kontaktorsak/updateKontaktorsak", {
                    kontaktorsak: data,
                });
            } else {
                await store.dispatch("operation/kontaktorsak/createKontaktorsak", {
                    kontaktorsak: data,
                });
            }

            store.dispatch("tabs/closeTab", tabId?.value);

            flashMessage("Kontaktorsak sparad");
        } catch (e: any) {
            handleErrorResponse(e, true);
        }
    };

    /**
     * Konverterar formulärdata till formatet som backend vill ha
     */
    function formDataConverter(formData: any) {
        const fields = Object.fromEntries(
            kontaktorsakDependents.fields.map((field: { name: string }) => [
                field.name,
                {
                    visible: Boolean(formData[`${field.name}.visible`]),
                    required: Boolean(formData[`${field.name}.required`]),
                },
            ])
        );

        const groups = Object.fromEntries(
            kontaktorsakDependents.groups.map((group: { name: string }) => [
                group.name,
                { visible: Boolean(formData[group.name]) },
            ])
        );

        return {
            id: kontaktorsakId,
            displayName: formData.displayName,
            code: formData.displayName,
            config: {
                fields,
                groups,
            },
        };
    }

    const cancelForm = ({ hasUnsavedData }: { hasUnsavedData: boolean }) => {
        if (hasUnsavedData) {
            emitter.emit("open-dialog", {
                type: "warning",
                description: "Du har osparad data. Vill du stänga utan att spara?",
                buttons: [
                    {
                        title: "Fortsätt redigera",
                        type: "secondary",
                    },
                    {
                        title: "Ja, stäng utan att spara",
                        type: "primary",
                        action: () => {
                            store.dispatch("tabs/closeTab", tabId?.value);
                        },
                    },
                ],
            });
        } else {
            store.dispatch("tabs/closeTab", tabId?.value);
        }
    };

    onBeforeMount(() => {
        const schemaTitle = schema.find((item) => item.component === "SchemaTitle");
        if (schemaTitle) {
            schemaTitle.content = kontaktorsakId ? "Redigera kontaktorsak" : "Skapa kontaktorsak";
        }

        setTabName();
    });

    const setTabName = () => {
        store.commit("tabs/setTabName", {
            id: tabId.value,
            name: kontaktorsakId ? "Redigera kontaktorsak" : "Skapa kontaktorsak",
        });
    };

    const hasUnsavedData = computed(() => {
        return formRef.value?.hasUnsavedData();
    });

    defineExpose({
        hasUnsavedData,
        tabId,
    });
</script>

<template>
    <KForm
        ref="formRef"
        id="kontaktorsak-form"
        :schema="schema"
        :initial-values-getter="initForm"
        @submit="submitForm"
        @cancel="cancelForm"
        :submit-content="kontaktorsakId ? 'Uppdatera' : 'Skapa'"
        cancel-content="Avbryt"
        :form-errors="formErrors"
        :errors="inputErrors"
    >
    </KForm>
</template>

<style scoped lang="scss">
    ::v-deep .k-label,
    ::v-deep .k-checkbox {
        min-width: 240px;
    }
    ::v-deep .k-section {
        // hårdkodar marginal för att separera sektionerna. Borde kanske göras mer generellt i KSection?
        margin-top: 2rem;
    }
</style>
